import {useContext} from 'react';
import type {SectionId} from 'course/section/utils';
import {useParams} from 'react-router-dom';
import {CourseIdContext} from 'contexts/CourseContext';
import {CourseId} from 'types/common';

/**
 * Gets the current course ID, or failing that attempts to infer it from the
 * URL path with a final fallback to "spr23".
 *
 * Reads from {@link CourseIdContext} rather than the `:courseId` URL param
 * directly, so it resolves correctly under routes that supply courseId via
 * {@link CourseProvider}'s explicit override prop instead of a literal URL
 * segment (e.g. an application short link). `CourseIdContext` carries just
 * the id string (unlike the full `CourseContext`), so this only re-renders
 * when the id itself changes.
 *
 * TODO: #986 - Refactor this fallback behavior.
 */
export const useCourseId = (): CourseId => {
  const courseId = useContext(CourseIdContext);

  if (!courseId) {
    const path = window.location.pathname;
    if (path.split('/').length > 1 && path.split('/')[1] === 'alumni') {
      return 'alumni';
    }
    return 'spr23';
  }

  return courseId;
};

/**
 * Returns the section ID from the URL params, or null if not found.
 */
export function useSectionId(): SectionId | null {
  const {sectionId} = useParams();
  return (sectionId ?? null) as SectionId | null;
}

/**
 * Returns the current course ID from {@link CourseIdContext}, or null if none
 * is resolved.
 * TODO: #986 - Remove this function and use `useCourseId` instead.
 */
export function useCourseIdWithoutFallback(): CourseId | null {
  const courseId = useContext(CourseIdContext);
  return courseId || null;
}
